import { useMemo, useState, useEffect } from 'react'
import { Filter, Eye, PauseCircle, PlayCircle, RotateCcw, Search, ChevronDown, ChevronUp } from 'lucide-react'
import toast from 'react-hot-toast'
import { collection, getDocs, doc, updateDoc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { db } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'

const PAGE_SIZE = 50

const GiftCards = () => {
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [filterType, setFilterType] = useState('All')
  const [filterDelivery, setFilterDelivery] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterDate, setFilterDate] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)

  const mapDocToCard = (docSnap) => {
    const data = docSnap.data()
    let balance = data.balance !== undefined && data.balance !== null ? Number(data.balance) : null
    if (balance === null) {
      balance = data.type === 'wellness' ? Number(data.amount || 0) : Number(data.price || 0)
      const giftCardRef = doc(db, 'giftCards', docSnap.id)
      updateDoc(giftCardRef, { balance }).catch(err => console.error(`Failed to update balance for ${docSnap.id}:`, err))
    }
    return {
      id: docSnap.id,
      type: data.type === 'wellness' ? 'Wellness' : (data.type === 'session' ? 'Session' : data.type),
      amount: data.amount || 0,
      category: data.sessionCategory || '',
      service: data.serviceType || '',
      price: data.price || 0,
      duration: data.duration || 0,
      balance: balance || 0,
      visitType: data.visitType || '',
      people: data.people != null ? Number(data.people) : '',
      creatorName: data.creatorName || '',
      creatorPhone: data.creatorPhone || '',
      recipientName: data.recipientName || '',
      recipientEmail: data.recipientEmail || '',
      recipientPhone: data.recipientPhone || '',
      message: data.message || '',
      deliveryMethod: data.deliveryMethod || 'digital',
      address: data.address || '',
      cardNumber: data.cardNumber || '',
      dateCreated: data.dateCreated ? new Date(data.dateCreated).toISOString().split('T')[0] : '',
      expiryDate: data.expiryDate ? new Date(data.expiryDate).toISOString().split('T')[0] : '',
      suspended: data.suspended || false,
      paymentReference: data.paymentReference || ''
    }
  }

  const fetchGiftCards = async (cursor = null, append = false) => {
      try {
        if (append) setLoadingMore(true)
        else setLoading(true)
        const giftCardsRef = collection(db, 'giftCards')
        const q = cursor
          ? query(giftCardsRef, orderBy('dateCreated', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
          : query(giftCardsRef, orderBy('dateCreated', 'desc'), limit(PAGE_SIZE))
        const snapshot = await getDocs(q)
        
        const giftCards = snapshot.docs.map(mapDocToCard)
        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
        setLastVisible(lastDoc)
        setHasMore(snapshot.docs.length === PAGE_SIZE)
        if (append) setCards(prev => [...prev, ...giftCards])
        else setCards(giftCards)
      } catch (error) {
        console.error('Error fetching gift cards:', error)
        setHasMore(false)
      } finally {
        if (append) setLoadingMore(false)
        else setLoading(false)
      }
    }

  const loadMoreGiftCards = () => {
    if (!lastVisible || loadingMore) return
    fetchGiftCards(lastVisible, true)
  }

  useEffect(() => {
    fetchGiftCards()
  }, [])

  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase()
    return cards.filter(c => {
      if (q) {
        const match = (s) => (s || '').toLowerCase().includes(q)
        const matchCard = match(c.cardNumber) || match(c.creatorName) || match(c.recipientName) || match(c.type) || match(c.category) || match(c.service) || match(c.creatorPhone) || match(c.recipientPhone)
        if (!matchCard) return false
      }
      const byType = filterType === 'All' || c.type === filterType
      const byDelivery = filterDelivery === 'All' || c.deliveryMethod === filterDelivery
      const byStatus = filterStatus === 'All' || (filterStatus === 'Suspended' ? c.suspended : !c.suspended)
      const byDate = !filterDate || c.dateCreated === filterDate
      return byType && byDelivery && byStatus && byDate
    })
  }, [cards, searchQuery, filterType, filterDelivery, filterStatus, filterDate])

  const toggleSuspend = async (id) => {
    try {
      const card = cards.find(c => c.id === id)
      if (!card) return
      
      const giftCardRef = doc(db, 'giftCards', id)
      await updateDoc(giftCardRef, {
        suspended: !card.suspended
      })
      
      // Update local state
      setCards(prev => prev.map(c => c.id === id ? { ...c, suspended: !c.suspended } : c))
    } catch (error) {
      console.error('Error updating gift card:', error)
      alert('Failed to update gift card status')
    }
  }

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Gift Cards</h1>
          <p className="text-gray-300 mt-1">Manage generated gift cards</p>
        </div>
        <button
          onClick={async () => {
            await fetchGiftCards()
            toast.success('Gift cards refreshed')
          }}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh gift cards"
        >
          <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
      <PageHelpButton
        videoId={3}
        title="Gift Cards management tutorial"
        youtubeUrl="https://www.youtube.com/embed/dWI106hWjU0"
      />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by card number, name, type, category..."
          className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
        />
        {searchQuery.trim() && <span className="text-xs text-gray-400 ml-2">{filtered.length} result(s)</span>}
      </div>

      {/* Filters (dropdown) */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setFilterOpen(prev => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Filter className="w-4 h-4 text-gray-300" />
            <span className="text-sm font-medium text-gray-200">Filters</span>
            {(filterType !== 'All' || filterDelivery !== 'All' || filterStatus !== 'All' || filterDate) && (
              <span className="text-xs text-purple-300">(active)</span>
            )}
          </div>
          {filterOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {filterOpen && (
          <div className="border-t border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Type</label>
                <select className="w-full input-field bg-gray-700 border-gray-600 text-white" value={filterType} onChange={e=>setFilterType(e.target.value)}>
                  <option>All</option>
                  <option>Session</option>
                  <option>Wellness</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Delivery</label>
                <select className="w-full input-field bg-gray-700 border-gray-600 text-white" value={filterDelivery} onChange={e=>setFilterDelivery(e.target.value)}>
                  <option>All</option>
                  <option value="digital">Digital</option>
                  <option value="physical">Physical</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Status</label>
                <select className="w-full input-field bg-gray-700 border-gray-600 text-white" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option>All</option>
                  <option>Active</option>
                  <option>Suspended</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Date Created</label>
                <input type="date" className="w-full input-field bg-gray-700 border-gray-600 text-white" value={filterDate} onChange={e=>setFilterDate(e.target.value)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Visit Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">People</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Session Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Service Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Creator Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Creator Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recipient Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recipient Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recipient Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Delivery</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Card Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Expiry Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Payment Reference</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Suspend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {loading ? (
                <tr>
                  <td colSpan="22" className="px-6 py-4 text-center text-gray-300">
                    Loading gift cards...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="22" className="px-6 py-4 text-center text-gray-300">
                    No gift cards found
                  </td>
                </tr>
              ) : (
                filtered.map(card => (
                <tr key={card.id} className="hover:bg-gray-700 bg-opacity-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">GHS {Number(card.amount).toFixed(2)}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                    card.balance > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    GHS {Number(card.balance).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.visitType ? String(card.visitType) : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.people !== '' && card.people != null ? String(card.people) : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.category || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.service || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{card.price ? `GHS ${Number(card.price).toFixed(2)}` : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.duration ? `${card.duration}m` : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.creatorName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.creatorPhone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.recipientName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.recipientEmail || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.recipientPhone || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.message || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">{card.deliveryMethod}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.address || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-mono">{card.cardNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.dateCreated || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{card.expiryDate || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-mono text-xs">{card.paymentReference || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={()=>toggleSuspend(card.id)}
                      className={`inline-flex items-center space-x-1 px-3 py-1 rounded-lg ${card.suspended ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'} text-white`}
                    >
                      {card.suspended ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                      <span>{card.suspended ? 'Unsuspend' : 'Suspend'}</span>
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && cards.length > 0 && (
          <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
            <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
            <button
              type="button"
              onClick={loadMoreGiftCards}
              disabled={!hasMore || loadingMore}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <RotateCcw className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : hasMore ? (
                <span>Load previous gift cards</span>
              ) : (
                <span>No more</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default GiftCards
